import { useCallback, useEffect, useRef } from 'react';

import env from '@eneco/common/env';
import useDC from '@eneco/dc/useDC';
import { useMediaQuery, useWindow } from '@eneco/sparky/hooks';
import { useRouter } from 'next/router';

import { buildPageViewEvent, PageViewProfile } from './buildPageViewEvent';
import { getViewportType } from './getViewportType';
import { buildPageLocation, normalizePagePath, normalizeReferrer } from './normalizePath';
import { NavigationPageViewOptions, VirtualPageViewOptions } from './PageViewContext';
import { useGTM } from '../gtm/hooks/useGTM';

export type UsePageViewTrackingProps = {
  clientId?: string | null;
  /** SHA-256 of `clientId`, hashed server-side (see @eneco/next/server). */
  clientIdEncoded?: string | null;
  /** Mirrors the `useLegacy` guard: only the NL business unit runs our GTM container. */
  enabled?: boolean;
  pageRelease?: string;
  pageType?: string;
  profile?: PageViewProfile | null;
};

/**
 * Emits the GA4 `page_view` event for both full page loads and SPA navigations,
 * so GTM needs no Custom JavaScript and our CSP can drop `unsafe-eval`.
 *
 * The event is inert until the GTM container is pointed at it: nothing in GTM
 * triggers on `page_view` yet, so it only lands in `window.dataLayer`. Turn off
 * GA4 Enhanced Measurement and the History Change trigger in the same container
 * change, or pageviews will be counted twice.
 */
export const usePageViewTracking = ({
  clientId,
  clientIdEncoded,
  enabled = true,
  pageRelease,
  pageType,
  profile,
}: UsePageViewTrackingProps) => {
  const { pushToDataLayer } = useGTM();
  const router = useRouter();
  const { isAuthenticated } = useDC();
  const md = useMediaQuery('md');
  const lg = useMediaQuery('lg');
  // Until the media query provider has mounted, md/lg are an SSR guess derived
  // from the user agent and cannot distinguish tablet. Wait for the real values.
  const hasWindow = useWindow();

  const isEnabled = enabled;

  /** The `page_location` of the previous page — becomes `page_referrer` on SPA navigation. */
  const lastPageLocationRef = useRef('');
  const lastPagePathRef = useRef<string | null>(null);
  const hasFiredInitialRef = useRef(false);

  const track = useCallback(
    ({ isSpa, virtualPath, knownPath }: { isSpa: boolean; virtualPath?: string; knownPath?: string }) => {
      if (!isEnabled || typeof window === 'undefined') return;

      const isVirtual = virtualPath !== undefined;
      // A virtual page, and a known-path real navigation (see trackPageView
      // below), have no committed browser URL yet at the time this runs, so
      // page_location is synthesised. Its pathname is normalized so callers
      // passing a trailing slash cannot make page_path and page_location
      // disagree, but the query string and hash are preserved — a flow
      // appends ?sc_trk=... for experiment attribution to the path it is
      // about to navigate to, and that belongs in page_location.
      const explicitPath = virtualPath ?? knownPath;
      const pagePath = normalizePagePath(explicitPath ?? window.location.href);
      const pageLocation =
        explicitPath !== undefined ? buildPageLocation(explicitPath, window.location.origin) : window.location.href;

      // Flows call router.replace() while initializing, which fires
      // routeChangeComplete for the path we already reported eagerly (see
      // trackPageView) or on mount. Virtual page views are explicit calls, so
      // they are never deduped.
      if (!isVirtual && pagePath === lastPagePathRef.current) return;

      // lastPageLocationRef can still be empty here: on the very first render of
      // a page that immediately rewrites its own URL (a flow landing page), a
      // known-path push can run before the mount effect below has recorded the
      // page the visitor actually landed on. Fall back to document.referrer so
      // page_referrer is never blank in that race.
      const pageReferrer = isSpa
        ? lastPageLocationRef.current || normalizeReferrer(document.referrer)
        : normalizeReferrer(document.referrer);

      const event = buildPageViewEvent({
        clientId,
        clientIdEncoded: clientIdEncoded ?? undefined,
        isAuthenticated: isAuthenticated ?? Boolean(clientId),
        isSpa,
        pageLocation,
        pageReferrer,
        pageRelease,
        pageType,
        platformRelease: env('BUILDNUMBER') || undefined,
        profile,
        viewportType: getViewportType({ md, lg }),
      });

      // Deliberately not updated for a virtual page view: the real location is
      // still the one the visitor is on, and it stays the referrer for whatever
      // they navigate to next.
      if (!isVirtual) {
        lastPagePathRef.current = pagePath;
        lastPageLocationRef.current = pageLocation;
      }

      pushToDataLayer(event);
    },
    [clientId, clientIdEncoded, isAuthenticated, isEnabled, lg, md, pageRelease, pageType, profile, pushToDataLayer],
  );

  // Keep the route-change subscription stable: re-subscribing on every render of
  // a component this high in the tree would be a lot of churn for no benefit.
  const trackRef = useRef(track);
  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  useEffect(() => {
    if (!isEnabled || !hasWindow || hasFiredInitialRef.current) return;
    hasFiredInitialRef.current = true;
    trackRef.current({ isSpa: false });
  }, [hasWindow, isEnabled]);

  useEffect(() => {
    if (!isEnabled) return;
    const handleRouteChange = () => trackRef.current({ isSpa: true });
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [isEnabled, router]);

  /**
   * Reports a page that has no URL of its own — a form-save confirmation, an
   * inline error state. `is_spa` is true because it is a virtual page view, not
   * a document load.
   */
  const trackVirtualPageView = useCallback(
    ({ pagePath }: VirtualPageViewOptions) => trackRef.current({ isSpa: true, virtualPath: pagePath }),
    [],
  );

  /**
   * For a caller that also performs the navigation itself. Fires synchronously,
   * for the path the caller is about to navigate to — do not wait for
   * `router.push`/`replace` to resolve.
   *
   * This exists because `routeChangeComplete` only fires after Next.js commits
   * the new URL, on a later tick. A caller that pushes another dataLayer event
   * (a funnel step, say) right after starting the navigation would otherwise
   * have that event land *before* this one, misattributing it to the page the
   * visitor is leaving. Calling this first — before starting the navigation —
   * guarantees the correct order regardless of how long the actual route
   * transition takes.
   *
   * The `routeChangeComplete` push that follows once the navigation resolves is
   * a no-op: its path matches what was already recorded here.
   */
  const trackPageView = useCallback(
    ({ pagePath }: NavigationPageViewOptions) => trackRef.current({ isSpa: true, knownPath: pagePath }),
    [],
  );

  return { trackVirtualPageView, trackPageView };
};
